import { useState, useEffect, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ChevronDown, ChevronUp, Eye, Plus, ArrowLeft, User, Phone, Loader2, Trash2, Download, FileText } from "lucide-react";
import { AutosuggestInput } from "./AutosuggestInput";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { downloadVisitHistory, viewVisitHistory } from "@/utils/documentGenerator";
import { DeleteVisitDialog } from "./DeleteVisitDialog";
type MedicalAlert = {
  type: string;
  description: string;
};
type StudentDetailsProps = {
  student: {
    id: string;
    surname: string;
    other_names?: string;
    class: string;
    stream?: string;
    medical_alerts: MedicalAlert[];
    tutor1_name?: string;
    tutor1_contact?: string;
    tutor2_name?: string;
    tutor2_contact?: string;
    emergency_contact_name?: string;
    emergency_contact_phone?: string;
    dormitory_mother?: string;
    dormitory_mother_contact?: string;
    section?: string;
    age?: number;
  };
  onBack?: () => void;
  onVisitSaved?: () => void;
};
export function StudentDetails({
  student: initialStudent,
  onBack,
  onVisitSaved
}: StudentDetailsProps) {
  const [treatmentType, setTreatmentType] = useState<string>("new");
  const [requiresFollowup, setRequiresFollowup] = useState(false);
  const [referStudent, setReferStudent] = useState(false);
  const [complaint, setComplaint] = useState("");
  const [complaints, setComplaints] = useState<string[]>([]);
  const [diagnosis, setDiagnosis] = useState("");
  const [diagnoses, setDiagnoses] = useState<string[]>([]);
  const [treatment, setTreatment] = useState("");
  const [treatments, setTreatments] = useState<string[]>([]);
  const [temperature, setTemperature] = useState<string>("36.2");
  const [temperatureError, setTemperatureError] = useState<string>("");
  const {
    toast
  } = useToast();
  const {
    user
  } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [showComplaints, setShowComplaints] = useState(true);
  const [showDiagnosis, setShowDiagnosis] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showContactInfo, setShowContactInfo] = useState(false);
  const [editingVisit, setEditingVisit] = useState<any>(null);
  const [studentAge, setStudentAge] = useState<string>(initialStudent.age?.toString() || '');

  // TISJ-specific vital signs fields
  const [pulse, setPulse] = useState<string>('');
  const [respiratoryRate, setRespiratoryRate] = useState<string>('');
  const [weight, setWeight] = useState<string>('');
  const [oxygenSaturation, setOxygenSaturation] = useState<string>('');
  const [painLevel, setPainLevel] = useState<string>('');

  // Validation states for TISJ fields
  const [pulseError, setPulseError] = useState<string>('');
  const [respiratoryError, setRespiratoryError] = useState<string>('');
  const [weightError, setWeightError] = useState<string>('');
  const [oxygenError, setOxygenError] = useState<string>('');
  const [painError, setPainError] = useState<string>('');

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [visitToDelete, setVisitToDelete] = useState<string>('');
  const [isDeleting, setIsDeleting] = useState(false);
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      if (!user?.schoolId) {
        toast({
          title: "Error",
          description: "School ID not found",
          variant: "destructive"
        });
        setIsSubmitting(false);
        return;
      }
      if (!nurseProfile?.id) {
        toast({
          title: "Error",
          description: "Nurse profile not found",
          variant: "destructive"
        });
        setIsSubmitting(false);
        return;
      }
      if (treatmentType !== "follow-up") {
        if (complaints.length === 0) {
          toast({
            title: "Error",
            description: "Please add at least one complaint",
            variant: "destructive"
          });
          setIsSubmitting(false);
          return;
        }
        if (!validateTemperature(temperature)) {
          toast({
            title: "Error",
            description: "Please correct the temperature value",
            variant: "destructive"
          });
          setIsSubmitting(false);
          return;
        }
        if (diagnoses.length === 0) {
          toast({
            title: "Error",
            description: "Please add at least one diagnosis",
            variant: "destructive"
          });
          setIsSubmitting(false);
          return;
        }
      }
      if (!activeTermDates || activeTermDates.length === 0) {
        toast({
          title: "Error",
          description: "No active terms found",
          variant: "destructive"
        });
        setIsSubmitting(false);
        return;
      }
      const now = new Date();
      const isWithinActiveTerm = activeTermDates.some(term => {
        const termStart = new Date(term.start_date);
        const termEnd = new Date(term.end_date);
        return now >= termStart && now <= termEnd;
      });
      if (!isWithinActiveTerm) {
        toast({
          title: "Error",
          description: "Cannot create visits outside of active term dates",
          variant: "destructive"
        });
        setIsSubmitting(false);
        return;
      }
      toast({
        title: "Saving",
        description: "Saving treatment record..."
      });
      const {
        data: activeYear,
        error: yearError
      } = await supabase.from("academic_years").select("id").eq("is_active", true).maybeSingle();
      if (yearError) throw yearError;
      if (!activeYear?.id) {
        toast({
          title: "Error",
          description: "No active academic year found",
          variant: "destructive"
        });
        return;
      }
      const {
        data: activeTerm,
        error: termError
      } = await supabase.from("terms").select("id, start_date, end_date").eq("is_active", true).eq("academic_year_id", activeYear.id).maybeSingle();
      if (termError) throw termError;
      if (!activeTerm?.id) {
        toast({
          title: "Error",
          description: "No active term found",
          variant: "destructive"
        });
        return;
      }
      const currentDate = new Date().toISOString();
      if (editingVisit) {
        const {
          error: updateError
        } = await supabase.from("visits").update({
          complaint: complaints.join(", "),
          diagnosis: diagnoses.join(", "),
          temperature: parseFloat(temperature),
          treatment: treatments.join(", "),
          requires_followup: requiresFollowup,
          school_id: user.schoolId
        }).eq('id', editingVisit.id);
        if (updateError) throw updateError;
        toast({
          title: "Success",
          description: "Visit updated successfully"
        });
        setEditingVisit(null);
        setComplaints([]);
        setDiagnoses([]);
        setTreatments([]);
        setTemperature("");
        setRequiresFollowup(false);
        refetchVisits();
      } else {
        // Prepare vital signs data for TISJ
        let vitalSignsData = null;
        if (schoolData?.prefix === "TISJ" && (pulse || respiratoryRate || weight || oxygenSaturation || painLevel)) {
          const vitalSigns = [];
          if (pulse) vitalSigns.push(`Pulse: ${pulse} bpm`);
          if (respiratoryRate) vitalSigns.push(`Respiratory Rate: ${respiratoryRate} bpm`);
          if (weight) vitalSigns.push(`Weight: ${weight} kg`);
          if (oxygenSaturation) vitalSigns.push(`Oxygen Saturation: ${oxygenSaturation}%`);
          if (painLevel) vitalSigns.push(`Pain Level: ${painLevel}/10`);
          
          if (vitalSigns.length > 0) {
            vitalSignsData = vitalSigns.join(", ");
          }
        }

        const visitData = {
          student_id: initialStudent.id,
          nurse_id: nurseProfile.id,
          complaint: treatmentType === "follow-up" ? "Follow-up visit" : complaints.join(", "),
          diagnosis: treatmentType === "follow-up" ? "Follow-up visit" : diagnoses.join(", "),
          temperature: parseFloat(temperature),
          treatment: treatments.join(", "),
          vital_signs: vitalSignsData,
          academic_year_id: activeYear.id,
          term_id: activeTerm.id,
          date: currentDate,
          requires_followup: requiresFollowup,
          school_id: user.schoolId
        };
        const {
          error: visitError
        } = await supabase.from("visits").insert([visitData]);
        if (visitError) throw visitError;
      }
      if (referStudent) {
        const referralData = {
          student_id: initialStudent.id,
          nurse_id: nurseProfile.id,
          reason: "Referred for medical attention",
          hospital: "School Clinic",
          academic_year_id: activeYear.id,
          term_id: activeTerm.id,
          school_id: user.schoolId
        };
        const {
          error: referralError
        } = await supabase.from("referrals").insert([referralData]);
        if (referralError) throw referralError;
        navigate('/nurse/referrals');
      } else {
        if (onBack) {
          onBack();
        }
      }
      if (onVisitSaved) {
        onVisitSaved();
      }
      queryClient.invalidateQueries({
        queryKey: ['todayVisits']
      });
      queryClient.invalidateQueries({
        queryKey: ['weekVisits']
      });
      queryClient.invalidateQueries({
        queryKey: ['followUpStudents']
      });
      queryClient.invalidateQueries({
        queryKey: ['studentVisits', initialStudent.id]
      });
      toast({
        title: "Success",
        description: "Visit records saved successfully"
      });
      setComplaints([]);
      setDiagnoses([]);
      setTreatments([]);
      setTemperature("");
      setRequiresFollowup(false);
    } catch (error) {
      console.error("Error saving visit:", error);
      toast({
        title: "Error",
        description: "Failed to save visit record",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [treatmentType, complaints, temperature, diagnoses, treatments, requiresFollowup, referStudent, user?.schoolId, toast, queryClient, navigate, onBack, onVisitSaved, editingVisit, initialStudent.id]);
  useEffect(() => {
    const handleKeyboardShortcuts = (e: KeyboardEvent) => {
      if (e.altKey && e.key.toLowerCase() === 's') {
        e.preventDefault();
        handleSubmit(new Event('submit') as any);
      }
    };
    const scrollToFocusedElement = () => {
      const activeElement = document.activeElement;
      if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')) {
        activeElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
    };
    const handleArrowNavigation = (e: KeyboardEvent) => {
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault();
        const inputs = Array.from(document.querySelectorAll('input[type="text"], input[type="number"], textarea')).filter(input => !input.hasAttribute('readonly') && (input as HTMLInputElement).value.length > 0);
        if (inputs.length === 0) return;
        const currentIndex = inputs.indexOf(document.activeElement as HTMLElement);
        let nextIndex;
        if (e.key === 'ArrowUp') {
          nextIndex = currentIndex > 0 ? currentIndex - 1 : inputs.length - 1;
        } else {
          nextIndex = currentIndex < inputs.length - 1 ? currentIndex + 1 : 0;
        }
        const nextInput = inputs[nextIndex] as HTMLElement;
        nextInput.focus();
        nextInput.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
    };
    document.addEventListener('keydown', handleKeyboardShortcuts);
    document.addEventListener('keydown', handleArrowNavigation);
    document.addEventListener('focus', scrollToFocusedElement, true);
    return () => {
      document.removeEventListener('keydown', handleKeyboardShortcuts);
      document.removeEventListener('keydown', handleArrowNavigation);
      document.removeEventListener('focus', scrollToFocusedElement, true);
    };
  }, [handleSubmit]);
  useEffect(() => {
    if (treatmentType === "follow-up") {
      setShowComplaints(false);
      setShowDiagnosis(false);
    } else {
      setShowComplaints(true);
      setShowDiagnosis(true);
    }
  }, [treatmentType]);
  const {
    data: nurseProfile
  } = useQuery({
    queryKey: ['nurseProfile', user?.id],
    queryFn: async () => {
      const {
        data,
        error
      } = await supabase.from('profiles').select('id').eq('user_id', user?.id).single();
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id
  });
  const {
    data: activeTermDates
  } = useQuery({
    queryKey: ['activeTermDates'],
    queryFn: async () => {
      const {
        data,
        error
      } = await supabase.from('terms').select('start_date, end_date').eq('is_active', true);
      if (error) throw error;
      return data;
    }
  });
  const validateTemperature = (value: string) => {
    const temp = parseFloat(value);
    if (isNaN(temp)) {
      setTemperatureError("Please enter a valid number");
      return false;
    }
    if (temp < 32.5 || temp > 42) {
      setTemperatureError("Temperature must be between 32.5°C and 42°C");
      return false;
    }
    setTemperatureError("");
    return true;
  };

  // TISJ-specific validation functions
  const validatePulse = (value: string) => {
    const pulse = parseFloat(value);
    if (isNaN(pulse)) {
      setPulseError("Please enter a valid number");
      return false;
    }
    if (pulse < 70 || pulse > 110) {
      setPulseError("Pulse must be between 70-110 beats per minute");
      return false;
    }
    setPulseError("");
    return true;
  };
  const validateRespiratoryRate = (value: string) => {
    const rate = parseFloat(value);
    if (isNaN(rate)) {
      setRespiratoryError("Please enter a valid number");
      return false;
    }
    if (rate < 14 || rate > 40) {
      setRespiratoryError("Respiratory rate must be between 14-40 breaths per minute");
      return false;
    }
    setRespiratoryError("");
    return true;
  };
  const validateWeight = (value: string) => {
    const weight = parseFloat(value);
    if (isNaN(weight)) {
      setWeightError("Please enter a valid number");
      return false;
    }
    if (weight < 15 || weight > 100) {
      setWeightError("Weight must be between 15-100 kilograms");
      return false;
    }
    setWeightError("");
    return true;
  };
  const validateOxygenSaturation = (value: string) => {
    const oxygen = parseFloat(value);
    if (isNaN(oxygen)) {
      setOxygenError("Please enter a valid number");
      return false;
    }
    if (oxygen < 92 || oxygen > 100) {
      setOxygenError("Oxygen saturation must be between 92-100%");
      return false;
    }
    setOxygenError("");
    return true;
  };
  const validatePainLevel = (value: string) => {
    const pain = parseFloat(value);
    if (isNaN(pain)) {
      setPainError("Please enter a valid number");
      return false;
    }
    if (pain < 0 || pain > 10) {
      setPainError("Pain level must be between 0-10");
      return false;
    }
    setPainError("");
    return true;
  };
  const handleTemperatureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTemperature(value);
    validateTemperature(value);
  };
  const handleEditVisit = async (visit: any) => {
    setTreatmentType("new");
    setComplaints(visit.complaint ? visit.complaint.split(", ") : []);
    setTemperature(visit.temperature.toString());
    setDiagnoses(visit.diagnosis ? visit.diagnosis.split(", ") : []);
    setTreatments(visit.treatment ? visit.treatment.split(", ") : []);
    setRequiresFollowup(!!visit.requires_followup);
    setEditingVisit(visit);
  };
  const addComplaint = (item: string) => {
    if (!complaints.includes(item)) {
      setComplaints([...complaints, item]);
    }
  };
  const removeComplaint = (item: string) => {
    setComplaints(complaints.filter(i => i !== item));
  };
  const addDiagnosis = (item: string) => {
    if (!diagnoses.includes(item)) {
      setDiagnoses([...diagnoses, item]);
    }
  };
  const removeDiagnosis = (item: string) => {
    setDiagnoses(diagnoses.filter(i => i !== item));
  };
  const addTreatment = (item: string) => {
    if (!treatments.includes(item)) {
      setTreatments([...treatments, item]);
    }
  };
  const removeTreatment = (item: string) => {
    setTreatments(treatments.filter(i => i !== item));
  };
  const handleDeleteVisit = (visitId: string) => {
    setVisitToDelete(visitId);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteVisit = async () => {
    if (!visitToDelete) return;
    setIsDeleting(true);
    
    try {
      const {
        error
      } = await supabase.from("visits").delete().eq('id', visitToDelete);
      if (error) throw error;
      toast({
        title: "Success",
        description: "Visit deleted successfully"
      });
      refetchVisits();
      queryClient.invalidateQueries({
        queryKey: ['todayVisits']
      });
      queryClient.invalidateQueries({
        queryKey: ['weekVisits']
      });
      queryClient.invalidateQueries({
        queryKey: ['followUpStudents']
      });
      queryClient.invalidateQueries({
        queryKey: ['studentVisits', initialStudent.id]
      });
    } catch (error) {
      console.error("Error deleting visit:", error);
      toast({
        title: "Error",
        description: "Failed to delete visit",
        variant: "destructive"
      });
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setVisitToDelete('');
    }
  };
  const {
    data: visitsData,
    refetch: refetchVisits
  } = useQuery({
    queryKey: ['studentVisits', initialStudent.id],
    queryFn: async () => {
      const {
        data,
        error
      } = await supabase.from('visits').select(`
          *,
          terms:term_id (
            id,
            name,
            is_active,
            academic_year:academic_year_id (
              id,
              name,
              is_active
            )
          )
        `).eq('student_id', initialStudent.id).order('date', {
        ascending: false
      });
      if (error) throw error;
      return data;
    }
  });
  const {
    data: currentStudent,
    refetch: refetchStudent
  } = useQuery({
    queryKey: ['student', initialStudent.id],
    queryFn: async () => {
      const {
        data,
        error
      } = await supabase.from('students').select('*').eq('id', initialStudent.id).single();
      if (error) throw error;
      return data;
    }
  });
  useEffect(() => {
    const channel = supabase.channel('student_updates').on('postgres_changes', {
      event: 'UPDATE',
      schema: 'public',
      table: 'students',
      filter: `id=eq.${initialStudent.id}`
    }, () => {
      refetchStudent();
      refetchVisits();
    }).subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [initialStudent.id, refetchStudent, refetchVisits]);
  const studentData = currentStudent || initialStudent;
  const medicalAlerts = Array.isArray(studentData.medical_alerts) ? studentData.medical_alerts : [];
  const {
    data: schoolData
  } = useQuery({
    queryKey: ['school', currentStudent?.school_id],
    queryFn: async () => {
      if (!currentStudent?.school_id) return null;
      const {
        data,
        error
      } = await supabase.from('schools').select('prefix').eq('id', currentStudent.school_id).single();
      if (error) throw error;
      return data;
    }
  });
  const isTISPBoarding = schoolData?.prefix === "TISP" && studentData.section === "BOARDING";
  const updateStudentAge = async (newAge: string) => {
    if (!user?.schoolId) {
      toast({
        title: "Error",
        description: "School ID not found",
        variant: "destructive"
      });
      return;
    }
    const parsedAge = newAge ? parseInt(newAge, 10) : null;
    try {
      const {
        error
      } = await supabase.from('students').update({
        age: parsedAge
      }).eq('id', initialStudent.id).eq('school_id', user.schoolId);
      if (error) {
        console.error("Error updating age:", error);
        toast({
          title: "Error",
          description: "Failed to update student age",
          variant: "destructive"
        });
        return;
      }
      toast({
        title: "Success",
        description: "Age updated successfully"
      });
      queryClient.invalidateQueries({
        queryKey: ['student', initialStudent.id]
      });
    } catch (error) {
      console.error("Error in updateStudentAge:", error);
      toast({
        title: "Error",
        description: "Failed to update student age",
        variant: "destructive"
      });
    }
  };
  return (
    <>
      <div className="space-y-6">
        <div className="fixed top-[150px] left-0 right-0 z-[10] mx-auto max-w-[1280px] px-8">
          <div className="rounded-lg border bg-background/90 backdrop-blur-md supports-[backdrop-filter]:bg-background/90 p-6 shadow-lg">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={onBack} className="hover:bg-primary/10">
                <ArrowLeft className="h-5 w-5 text-primary" />
              </Button>
              <div className="space-y-1">
                <h2 className="text-2xl font-bold">
                  {studentData.surname}
                  {studentData.other_names ? `, ${studentData.other_names}` : ""}
                </h2>
                <div className="text-sm text-muted-foreground">
                  Class: {studentData.class}
                  {studentData.stream ? ` Stream: ${studentData.stream}` : ""}
                </div>
              </div>
            </div>

          <Collapsible open={showContactInfo} onOpenChange={setShowContactInfo} className="mt-4 space-y-2">
            <CollapsibleTrigger className="flex w-full items-center justify-between rounded-md border p-2 font-medium hover:bg-muted">
              <span>Contact Information</span>
              {showContactInfo ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {isTISPBoarding && <div className="space-y-2">
                    <h3 className="font-medium">Dormitory Mother</h3>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <span>{studentData.dormitory_mother || 'Not provided'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-muted-foreground" />
                        <span>{studentData.dormitory_mother_contact || 'Not provided'}</span>
                      </div>
                    </div>
                  </div>}
                <div className="space-y-2">
                  <h3 className="font-medium">Emergency Contact</h3>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-muted-foreground" />
                      <span>{studentData.emergency_contact_name || 'Not provided'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <span>{studentData.emergency_contact_phone || 'Not provided'}</span>
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <h3 className="font-medium">Tutor 1</h3>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-muted-foreground" />
                      <span>{studentData.tutor1_name || 'Not provided'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <span>{studentData.tutor1_contact || 'Not provided'}</span>
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <h3 className="font-medium">Tutor 2</h3>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-muted-foreground" />
                      <span>{studentData.tutor2_name || 'Not provided'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <span>{studentData.tutor2_contact || 'Not provided'}</span>
                    </div>
                  </div>
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>
      </div>

      <div className="pt-[250px]">
        <div className="grid gap-6 md:grid-cols-2 relative z-[20]">
          <Card className="p-6 bg-background/90 backdrop-blur-md supports-[backdrop-filter]:bg-background/90">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="treatmentType">Treatment Type</Label>
                <Select value={treatmentType} onValueChange={setTreatmentType}>
                  <SelectTrigger className="bg-background border">
                    <SelectValue placeholder="Select treatment type" />
                  </SelectTrigger>
                  <SelectContent className="bg-background border shadow-lg">
                    <SelectItem value="new">New Treatment</SelectItem>
                    <SelectItem value="follow-up">Follow-up</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="temperature">Temperature (°C)</Label>
                  <Input id="temperature" type="number" step="0.1" value={temperature} onChange={handleTemperatureChange} placeholder="Enter temperature in °C" className={temperatureError ? "border-red-500" : ""} />
                  {temperatureError && <p className="text-sm text-red-500 mt-1">{temperatureError}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="age">Age</Label>
                  <Input id="age" type="number" min="0" max="100" value={studentAge} onChange={e => {
                  const newAge = e.target.value;
                  setStudentAge(newAge);
                  updateStudentAge(newAge);
                }} placeholder="Enter age" className="w-full" />
                </div>
              </div>

              {/* TISJ-specific vital signs fields */}
              {schoolData?.prefix === "TISJ" && <div className="space-y-4">
                  <div className="text-sm font-medium text-primary">Vital  Assessment</div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="pulse">Pulse (70-110 bpm)</Label>
                      <Input id="pulse" type="number" step="1" min="70" max="110" value={pulse} onChange={e => {
                    setPulse(e.target.value);
                    validatePulse(e.target.value);
                  }} placeholder="Enter pulse rate" className={pulseError ? "border-red-500" : ""} />
                      {pulseError && <p className="text-sm text-red-500 mt-1">{pulseError}</p>}
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="respiratoryRate">Respiratory Rate (14-40 breaths/min)</Label>
                      <Input id="respiratoryRate" type="number" step="1" min="14" max="40" value={respiratoryRate} onChange={e => {
                    setRespiratoryRate(e.target.value);
                    validateRespiratoryRate(e.target.value);
                  }} placeholder="Enter respiratory rate" className={respiratoryError ? "border-red-500" : ""} />
                      {respiratoryError && <p className="text-sm text-red-500 mt-1">{respiratoryError}</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="weight">Weight (15-100 kg)</Label>
                      <Input id="weight" type="number" step="0.1" min="15" max="100" value={weight} onChange={e => {
                    setWeight(e.target.value);
                    validateWeight(e.target.value);
                  }} placeholder="Enter weight in kg" className={weightError ? "border-red-500" : ""} />
                      {weightError && <p className="text-sm text-red-500 mt-1">{weightError}</p>}
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="oxygenSaturation">Oxygen Saturation (92-100%)</Label>
                      <Input id="oxygenSaturation" type="number" step="1" min="92" max="100" value={oxygenSaturation} onChange={e => {
                    setOxygenSaturation(e.target.value);
                    validateOxygenSaturation(e.target.value);
                  }} placeholder="Enter oxygen saturation" className={oxygenError ? "border-red-500" : ""} />
                      {oxygenError && <p className="text-sm text-red-500 mt-1">{oxygenError}</p>}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="painLevel">Pain Level Scale (0-10)</Label>
                    <Input id="painLevel" type="number" step="1" min="0" max="10" value={painLevel} onChange={e => {
                  setPainLevel(e.target.value);
                  validatePainLevel(e.target.value);
                }} placeholder="Enter pain level (0=no pain, 10=severe pain)" className={painError ? "border-red-500" : ""} />
                    {painError && <p className="text-sm text-red-500 mt-1">{painError}</p>}
                  </div>
                </div>}

              <Collapsible open={showComplaints} onOpenChange={setShowComplaints}>
                <CollapsibleTrigger className="flex w-full items-center justify-between">
                  <Label>Complaints</Label>
                  <ChevronDown className={`h-4 w-4 transition-transform ${showComplaints ? 'rotate-180' : ''}`} />
                </CollapsibleTrigger>
                <CollapsibleContent className="pt-2">
                  <AutosuggestInput type="COMPLAINT" value={complaint} onChange={setComplaint} placeholder="Enter complaints" onAddItem={addComplaint} selectedItems={complaints} onRemoveItem={removeComplaint} shortcutKey="c" />
                </CollapsibleContent>
              </Collapsible>

              <Collapsible open={showDiagnosis} onOpenChange={setShowDiagnosis}>
                <CollapsibleTrigger className="flex w-full items-center justify-between">
                  <Label>Diagnosis</Label>
                  <ChevronDown className={`h-4 w-4 transition-transform ${showDiagnosis ? 'rotate-180' : ''}`} />
                </CollapsibleTrigger>
                <CollapsibleContent className="pt-2">
                  <AutosuggestInput type="DIAGNOSIS" value={diagnosis} onChange={setDiagnosis} placeholder="Enter diagnosis" onAddItem={addDiagnosis} selectedItems={diagnoses} onRemoveItem={removeDiagnosis} shortcutKey="d" />
                </CollapsibleContent>
              </Collapsible>

              <div className="space-y-2">
                <Label htmlFor="treatment">Treatment</Label>
                <AutosuggestInput type="TREATMENT" value={treatment} onChange={setTreatment} placeholder="Enter treatment" onAddItem={addTreatment} selectedItems={treatments} onRemoveItem={removeTreatment} shortcutKey="r" />
              </div>

              <div className="space-y-4">
                <TooltipProvider>
                  <div className="flex items-center space-x-2">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="flex items-center space-x-2">
                          <Checkbox id="followup" checked={requiresFollowup} onCheckedChange={checked => setRequiresFollowup(!!checked)} />
                          <label htmlFor="followup" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                            Requires Follow-up (Alt + F)
                          </label>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Mark this visit for follow-up</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="flex items-center space-x-2">
                          <Checkbox id="refer" checked={referStudent} onCheckedChange={checked => setReferStudent(!!checked)} />
                          <label htmlFor="refer" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                            Refer Student
                          </label>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Refer student for further medical attention</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                </TooltipProvider>
              </div>

              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button type="submit" className="w-full" disabled={isSubmitting}>
                      {isSubmitting ? <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Saving...
                        </> : 'Save Treatment (Alt + S)'}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Save the treatment record</p>
                    <p className="text-xs text-muted-foreground">
                      All required fields must be filled
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </form>
          </Card>

          <Card className="p-6 bg-background/90 backdrop-blur-md supports-[backdrop-filter]:bg-background/90">
            <div className="space-y-6">
              <Collapsible defaultOpen>
                <CollapsibleTrigger className="flex items-center gap-2 w-full text-left">
                  <div className={`w-2 h-2 rounded-full ${medicalAlerts && medicalAlerts.length > 0 ? 'bg-red-500' : 'bg-green-500'}`} />
                  <h3 className="text-lg font-semibold">Medical Alerts</h3>
                  <ChevronDown className="h-4 w-4 ml-auto" />
                </CollapsibleTrigger>
                <CollapsibleContent className="pt-4">
                  {medicalAlerts && medicalAlerts.length > 0 ? <div className="space-y-2">
                      {medicalAlerts.map((alert, index) => <div key={index} className="border rounded-md p-3">
                          <div className="font-medium text-sm">{alert.type.replace(/_/g, ' ')}</div>
                          <div className="text-sm text-muted-foreground">{alert.description}</div>
                        </div>)}
                    </div> : <div className="text-sm text-muted-foreground">No medical alerts</div>}
                </CollapsibleContent>
              </Collapsible>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">Visit History</h3>
                  
                  {visitsData && visitsData.length > 0 && <div className="flex items-center gap-2">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Preview complete visit history</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button variant="outline" size="sm" className="flex items-center gap-1" onClick={() => downloadVisitHistory(currentStudent || initialStudent, visitsData)}>
                              <Download className="h-4 w-4" />
                              <span>Download</span>
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Download complete visit history as DOCX</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>}
                </div>
                
                {visitsData && visitsData.length > 0 ? <div className="space-y-4">
                    {Object.entries(visitsData.reduce((acc, visit) => {
                  const termInfo = visit.terms;
                  const key = `${termInfo.name} - ${termInfo.academic_year.name}`;
                  if (!acc[key]) {
                    acc[key] = [];
                  }
                  acc[key].push(visit);
                  return acc;
                }, {} as Record<string, typeof visitsData>)).map(([termTitle, termVisits], index) => <Collapsible key={termTitle} defaultOpen={index === 0}>
                        <CollapsibleTrigger className="w-full flex items-center justify-between p-4 bg-muted/50 rounded-lg hover:bg-muted/70">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{termTitle}</span>
                            <span className="text-sm text-muted-foreground">
                              ({termVisits.length} {termVisits.length === 1 ? 'visit' : 'visits'})
                            </span>
                          </div>
                          <ChevronDown className="h-4 w-4" />
                        </CollapsibleTrigger>
                        <CollapsibleContent className="pt-4 space-y-4">
                          {termVisits.map((visit, visitIndex) => <Collapsible key={visit.id} defaultOpen={visitIndex === 0}>
                              <CollapsibleTrigger className="w-full">
                                <div className="flex justify-between items-center border-t pt-4">
                                  <div className="space-y-1">
                                    <span className="font-medium">
                                      {format(new Date(visit.date), 'MMMM do, yyyy')}
                                    </span>
                                    <div className="text-sm text-muted-foreground">
                                      {format(new Date(visit.date), 'h:mm a')}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Button variant="ghost" size="sm" onClick={e => {
                              e.stopPropagation();
                              handleEditVisit(visit);
                            }} className="text-primary hover:bg-primary/10 hover:text-primary flex items-center gap-1">
                                      <Eye className="h-4 w-4" />
                                      <span>Edit</span>
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={e => {
                              e.stopPropagation();
                              handleDeleteVisit(visit.id);
                            }} className="text-destructive hover:bg-destructive/10 hover:text-destructive flex items-center gap-1">
                                      <Trash2 className="h-4 w-4" />
                                      <span>Delete</span>
                                    </Button>
                                    <ChevronDown className="h-4 w-4" />
                                  </div>
                                </div>
                              </CollapsibleTrigger>
                              <CollapsibleContent className="space-y-2 mt-2 ml-4">
                                <div>
                                  <div className="font-medium">Complaint</div>
                                  <div className="text-sm text-muted-foreground">{visit.complaint}</div>
                                </div>
                                <div>
                                  <div className="font-medium">Diagnosis</div>
                                  <div className="text-sm text-muted-foreground">{visit.diagnosis}</div>
                                </div>
                                <div>
                                  <div className="font-medium">Temperature</div>
                                  <div className="text-sm text-muted-foreground">{visit.temperature}°C</div>
                                </div>
                                <div>
                                  <div className="font-medium">Treatment</div>
                                  <div className="text-sm text-muted-foreground">{visit.treatment}</div>
                                </div>
                                {visit.vital_signs && (
                                  <div>
                                    <div className="font-medium">Vital Signs</div>
                                    <div className="text-sm text-muted-foreground">{visit.vital_signs}</div>
                                  </div>
                                )}
                              </CollapsibleContent>
                            </Collapsible>)}
                        </CollapsibleContent>
                      </Collapsible>)}
                  </div> : <div className="text-sm text-muted-foreground text-center py-4">
                    No visit history available
                  </div>}
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>

    <DeleteVisitDialog
      open={deleteDialogOpen}
      onOpenChange={setDeleteDialogOpen}
      onConfirm={confirmDeleteVisit}
      isDeleting={isDeleting}
    />
    </>
  );
}